import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ShieldCheck, Plus, Trash2, Crown, KeyRound, Mail, AlertTriangle, Pencil, SlidersHorizontal, LogIn, Activity, Users as UsersIcon, Palette, Upload, Link as LinkIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_LABELS = {
  clients: "Clientes",
  subscriptions: "Pagos / Suscripciones",
  landing: "Landing Page",
  emails: "Plantillas de Email",
  payments: "Pasarelas de pago",
  broadcast: "Difusión masiva",
  branding: "Branding global",
};

export default function AdminSuperAdmins() {
  const { admin, getAdminAxios } = useOutletContext();
  const navigate = useNavigate();
  const isRoot = !!(admin && admin.is_root_super_admin);

  // White-label safety: sub super admins should never see this page. If they
  // arrive here by typing the URL, send them back to their clients list.
  useEffect(() => {
    if (admin && !isRoot) {
      navigate("/admin/clients", { replace: true });
    }
  }, [admin, isRoot, navigate]);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [permsEditing, setPermsEditing] = useState(null);
  const [savingPerms, setSavingPerms] = useState(false);
  const [brandingEditing, setBrandingEditing] = useState(null);
  const [savingBranding, setSavingBranding] = useState(false);
  const [activityFor, setActivityFor] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", can_create_super_admins: false, sa_perms: {} });
  const [error, setError] = useState("");

  const me = admin || {};
  const canCreate = !!me.can_create_super_admins;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAdminAxios().get(`${API}/admin/super-admins`);
      setList(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [getAdminAxios]);

  useEffect(() => { refresh(); }, [refresh]);

  const submitCreate = async () => {
    if (!form.email || !form.password) { setError("Email y contraseña son obligatorios"); return; }
    if (form.password.length < 6) { setError("La contraseña debe tener al menos 6 caracteres"); return; }
    setCreating(true); setError("");
    try {
      await getAdminAxios().post(`${API}/admin/super-admins`, form);
      setCreateOpen(false);
      setForm({ email: "", password: "", name: "", can_create_super_admins: false, sa_perms: {} });
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setCreating(false); }
  };

  const toggleCanCreate = async (u) => {
    if (!canCreate) return;
    try {
      await getAdminAxios().put(`${API}/admin/super-admins/${u.id}/can-create`, { can_create_super_admins: !u.can_create_super_admins });
      await refresh();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const remove = async (u) => {
    if (!canCreate || u.id === me.id) return;
    if (!window.confirm(`¿Eliminar al super admin ${u.email}? Esta acción no se puede deshacer.`)) return;
    try {
      await getAdminAxios().delete(`${API}/admin/super-admins/${u.id}`);
      await refresh();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const submitEdit = async () => {
    setSavingEdit(true); setError("");
    try {
      const patch = { name: editing.name, email: editing.email };
      if (editing.password && editing.password.length > 0) {
        if (editing.password.length < 6) { setError("La contraseña debe tener al menos 6 caracteres"); setSavingEdit(false); return; }
        patch.password = editing.password;
      }
      await getAdminAxios().put(`${API}/admin/super-admins/${editing.id}`, patch);
      setEditing(null);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSavingEdit(false); }
  };

  const submitPerms = async () => {
    setSavingPerms(true); setError("");
    try {
      await getAdminAxios().put(`${API}/admin/super-admins/${permsEditing.id}/perms`, { sa_perms: permsEditing.sa_perms });
      setPermsEditing(null);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSavingPerms(false); }
  };

  const submitBranding = async () => {
    setSavingBranding(true); setError("");
    try {
      await getAdminAxios().put(`${API}/admin/super-admins/${brandingEditing.id}/branding`, {
        logo_url: brandingEditing.logo_url || "",
        accent_color: brandingEditing.accent_color || "",
        brand_name: brandingEditing.brand_name || "",
      });
      // Save subdomain separately (different endpoint with validation)
      if ((brandingEditing.subdomain || "") !== (brandingEditing._initial_subdomain || "")) {
        await getAdminAxios().put(`${API}/admin/super-admins/${brandingEditing.id}/subdomain`, {
          subdomain: brandingEditing.subdomain || "",
        });
      }
      setBrandingEditing(null);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSavingBranding(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("La imagen no puede pesar más de 5 MB"); return; }
    setSavingBranding(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", "sa-logo");
      const { data } = await getAdminAxios().post(`${API}/admin/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBrandingEditing({ ...brandingEditing, logo_url: data.url });
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSavingBranding(false); }
  };

  const copyTenantLink = (slug) => {
    const base = window.location.origin;
    const link = `${base}/?tenant=${slug}`;
    navigator.clipboard.writeText(link).then(() => {
      window.alert("Link copiado al portapapeles:\n\n" + link + "\n\nTambién podés usar:\n" + `${base}/t/${slug}/login`);
    }).catch(() => {
      window.prompt("Copiá este link:", link);
    });
  };

  const impersonate = async (u) => {
    if (!isRoot || u.id === me.id) return;
    if (!window.confirm(`¿Iniciar sesión como ${u.email}? Vas a ver el panel desde su perspectiva. Después podés volver a tu cuenta con el botón de la barra superior.`)) return;
    try {
      const { data } = await getAdminAxios().post(`${API}/admin/impersonate/${u.id}`);
      // Save my own session so I can come back
      const myToken = localStorage.getItem("wa_admin_token");
      const myUser = localStorage.getItem("wa_admin_user");
      localStorage.setItem("wa_admin_root_token", myToken);
      localStorage.setItem("wa_admin_root_user", myUser);
      // Switch to impersonated user
      localStorage.setItem("wa_admin_token", data.token);
      localStorage.setItem("wa_admin_user", JSON.stringify({ id: data.id, email: data.email, name: data.name, role: data.role }));
      window.location.href = "/admin/dashboard";
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const openActivity = async (u) => {
    setActivityFor(u);
    setActivityData(null);
    try {
      const { data } = await getAdminAxios().get(`${API}/admin/super-admins/${u.id}/activity`);
      setActivityData(data);
    } catch (e) {
      setActivityData({ error: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-super-admins-page">
      <div className="flex items-start justify-between gap-3 mb-8 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
            <ShieldCheck className="w-6 h-6" /> Super Admins
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Solo el <strong className="text-amber-300">super admin principal</strong> puede asignar permisos por sección, ver la actividad de cada sub-admin e iniciar sesión como ellos. Los sub-admins solo ven los clientes que ellos crearon.
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setCreateOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="create-super-admin-btn">
            <Plus className="w-4 h-4 mr-2" /> Crear super admin
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-300">⚠️ {error}</div>
      )}

      <Card className="bg-[#13112b] border-indigo-900/30">
        <CardHeader>
          <CardTitle className="text-white text-base">Super admins activos ({list.length})</CardTitle>
          <CardDescription className="text-slate-400">Tocá los chips de sección para ver qué tiene habilitado cada uno.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
          ) : list.length === 0 ? (
            <p className="text-sm text-slate-500">Sin super admins.</p>
          ) : (
            <div className="space-y-3">
              {list.map((u) => {
                const isMe = u.id === me.id;
                const targetIsRoot = !!u.is_root;
                const enabledSections = Object.entries(u.sa_perms || {}).filter(([, v]) => v).map(([k]) => k);
                return (
                  <div key={u.id} className="border border-white/10 rounded-lg p-4 bg-black/20" data-testid={`sa-row-${u.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                          {targetIsRoot
                            ? <Crown className="w-5 h-5 text-amber-400" />
                            : <ShieldCheck className="w-5 h-5 text-slate-300" />}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate flex items-center gap-2">
                            {u.name || u.email}
                            {isMe && <span className="text-xs text-amber-300">(tú)</span>}
                            {targetIsRoot && <span className="text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded">root</span>}
                          </p>
                          <p className="text-xs text-slate-400 truncate flex items-center gap-1"><Mail className="w-3 h-3" /> {u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 flex-wrap">
                        {/* Activity (root only, not on self) */}
                        {isRoot && !isMe && (
                          <Button variant="outline" size="sm" onClick={() => openActivity(u)} className="border-indigo-400/30 text-indigo-300 hover:bg-indigo-400/10" data-testid={`sa-activity-${u.id}`}>
                            <Activity className="w-3 h-3 mr-1" /> Actividad
                          </Button>
                        )}
                        {/* Login as / Impersonate (root only, not on self) */}
                        {isRoot && !isMe && (
                          <Button variant="outline" size="sm" onClick={() => impersonate(u)} className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10" data-testid={`sa-impersonate-${u.id}`}>
                            <LogIn className="w-3 h-3 mr-1" /> Iniciar sesión
                          </Button>
                        )}
                        {/* Edit perms (root only, not on root target — root always has everything) */}
                        {isRoot && !targetIsRoot && (
                          <Button variant="outline" size="sm" onClick={() => setPermsEditing({ id: u.id, email: u.email, sa_perms: { ...(u.sa_perms || {}) } })} className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10" data-testid={`sa-perms-${u.id}`}>
                            <SlidersHorizontal className="w-3 h-3 mr-1" /> Permisos
                          </Button>
                        )}
                        {/* Edit branding (root only) */}
                        {isRoot && !targetIsRoot && (
                          <Button variant="outline" size="sm" onClick={() => setBrandingEditing({ id: u.id, email: u.email, subdomain: u.subdomain || "", _initial_subdomain: u.subdomain || "", ...(u.sa_branding || {}) })} className="border-pink-500/30 text-pink-300 hover:bg-pink-500/10" data-testid={`sa-branding-${u.id}`}>
                            <Palette className="w-3 h-3 mr-1" /> Branding
                          </Button>
                        )}
                        {(canCreate || isMe) && (
                          <Button variant="outline" size="sm" onClick={() => setEditing({ id: u.id, name: u.name, email: u.email, password: "" })} className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10" data-testid={`sa-edit-${u.id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                        )}
                        {canCreate && !isMe && !targetIsRoot && (
                          <Button variant="outline" size="sm" onClick={() => remove(u)} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`sa-delete-${u.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* Section chips */}
                    {!targetIsRoot && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {Object.entries(SECTION_LABELS).map(([key, label]) => {
                          const on = (u.sa_perms || {})[key];
                          return (
                            <span key={key} className={`text-[10px] px-2 py-0.5 rounded-full border ${on ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-white/10 bg-white/5 text-slate-500"}`}>
                              {on ? "✓ " : "○ "}{label}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {targetIsRoot && (
                      <div className="mt-3 text-xs text-amber-300/80 flex items-center gap-1.5"><Crown className="w-3 h-3" /> Acceso total a todas las secciones.</div>
                    )}
                    {!targetIsRoot && (
                      <div className="mt-2 flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          <Label className="text-xs text-slate-400">Puede crear super admins</Label>
                          <Switch
                            checked={!!u.can_create_super_admins}
                            disabled={!canCreate}
                            onCheckedChange={() => toggleCanCreate(u)}
                            className="data-[state=checked]:bg-amber-500"
                            data-testid={`sa-toggle-can-create-${u.id}`}
                          />
                        </div>
                        {enabledSections.length === 0 && (
                          <span className="text-[11px] text-amber-300/60 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> No tiene ninguna sección habilitada — verá solo "Dashboard" vacío.</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#13112b] border-indigo-900/30 text-white max-w-2xl" data-testid="create-super-admin-dialog">
          <DialogHeader>
            <DialogTitle>Crear super admin</DialogTitle>
            <DialogDescription className="text-slate-400">
              Por defecto NO podrá crear más super admins ni ver ninguna sección. Activá los toggles según lo que quieras delegarle.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Nombre</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="new-sa-name" />
              </div>
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="new-sa-email" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-slate-300 flex items-center gap-1"><KeyRound className="w-3 h-3" /> Contraseña (mín. 6)</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="new-sa-password" />
              </div>
            </div>
            {isRoot && (
              <div className="rounded-lg border border-amber-500/20 bg-white/5 p-3 space-y-2">
                <Label className="text-slate-200 text-sm">Secciones habilitadas</Label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(SECTION_LABELS).map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between bg-black/20 rounded px-3 py-1.5">
                      <span className="text-xs text-slate-300">{label}</span>
                      <Switch
                        checked={!!form.sa_perms[key]}
                        onCheckedChange={(v) => setForm({ ...form, sa_perms: { ...form.sa_perms, [key]: v } })}
                        className="data-[state=checked]:bg-emerald-500"
                        data-testid={`new-sa-perm-${key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-amber-500/20">
              <div className="pr-3">
                <Label className="text-slate-200">Puede crear más super admins</Label>
                <p className="text-xs text-slate-500 mt-1">Recomendado dejar OFF salvo que confíes al 100%.</p>
              </div>
              <Switch checked={form.can_create_super_admins} onCheckedChange={(v) => setForm({ ...form, can_create_super_admins: v })} className="data-[state=checked]:bg-amber-500" data-testid="new-sa-can-create" />
            </div>
            {error && <p className="text-xs text-red-400">⚠️ {error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={submitCreate} disabled={creating} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="submit-create-sa">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#13112b] border-indigo-900/30 text-white" data-testid="edit-sa-dialog">
          <DialogHeader>
            <DialogTitle>Editar super admin</DialogTitle>
            <DialogDescription className="text-slate-400">Dejá la contraseña vacía si no querés modificarla.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Nombre</Label>
                <Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="edit-sa-name" />
              </div>
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="edit-sa-email" />
              </div>
              <div>
                <Label className="text-slate-300 flex items-center gap-1"><KeyRound className="w-3 h-3" /> Nueva contraseña (mín. 6, opcional)</Label>
                <Input type="password" placeholder="Dejá vacío para mantener la actual" value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="edit-sa-password" />
              </div>
              {error && <p className="text-xs text-red-400">⚠️ {error}</p>}
              {editing.id === me.id && (
                <p className="text-xs text-amber-300">ℹ️ Estás editando tu propia cuenta — vas a tener que volver a iniciar sesión si cambiás email o contraseña.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={submitEdit} disabled={savingEdit} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="submit-edit-sa">
              {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Pencil className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Perms dialog (root-only) */}
      <Dialog open={!!permsEditing} onOpenChange={(v) => !v && setPermsEditing(null)}>
        <DialogContent className="bg-[#13112b] border-indigo-900/30 text-white max-w-lg" data-testid="edit-sa-perms-dialog">
          <DialogHeader>
            <DialogTitle>Permisos de sección</DialogTitle>
            <DialogDescription className="text-slate-400">
              Marcá qué módulos puede ver y editar <strong className="text-amber-300">{permsEditing?.email}</strong>. Los clientes solo verán los que cree este sub-admin.
            </DialogDescription>
          </DialogHeader>
          {permsEditing && (
            <div className="space-y-2">
              {Object.entries(SECTION_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center justify-between bg-white/5 rounded px-4 py-2 border border-white/10">
                  <Label className="text-sm text-slate-200">{label}</Label>
                  <Switch
                    checked={!!permsEditing.sa_perms[key]}
                    onCheckedChange={(v) => setPermsEditing({ ...permsEditing, sa_perms: { ...permsEditing.sa_perms, [key]: v } })}
                    className="data-[state=checked]:bg-emerald-500"
                    data-testid={`edit-sa-perm-${key}`}
                  />
                </div>
              ))}
              {error && <p className="text-xs text-red-400">⚠️ {error}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermsEditing(null)}>Cancelar</Button>
            <Button onClick={submitPerms} disabled={savingPerms} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="submit-edit-sa-perms">
              {savingPerms ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <SlidersHorizontal className="w-4 h-4 mr-2" />}
              Guardar permisos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Branding dialog (root-only) */}
      <Dialog open={!!brandingEditing} onOpenChange={(v) => !v && setBrandingEditing(null)}>
        <DialogContent className="bg-[#13112b] border-indigo-900/30 text-white max-w-lg" data-testid="edit-sa-branding-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Palette className="w-4 h-4" /> Branding del sub admin</DialogTitle>
            <DialogDescription className="text-slate-400">
              Logo y colores del panel de <strong className="text-pink-300">{brandingEditing?.email}</strong>. El favicon NO se modifica.
            </DialogDescription>
          </DialogHeader>
          {brandingEditing && (
            <div className="space-y-3">
              <div>
                <Label className="text-slate-300">Nombre de marca (reemplaza "Admin Panel")</Label>
                <Input value={brandingEditing.brand_name || ""} onChange={(e) => setBrandingEditing({ ...brandingEditing, brand_name: e.target.value })} placeholder="Ej: Acme Panel" className="bg-white/5 border-white/10 text-white" data-testid="sa-branding-name" />
              </div>
              <div>
                <Label className="text-slate-300">Logo</Label>
                <div className="flex items-start gap-3">
                  {brandingEditing.logo_url ? (
                    <img src={brandingEditing.logo_url} alt="preview" className="w-16 h-16 rounded-lg object-cover border border-white/10 shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-white/5 border border-dashed border-white/20 flex items-center justify-center shrink-0">
                      <Palette className="w-5 h-5 text-slate-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <Input
                      value={brandingEditing.logo_url || ""}
                      onChange={(e) => setBrandingEditing({ ...brandingEditing, logo_url: e.target.value })}
                      placeholder="Pegá URL o subí archivo"
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="sa-branding-logo"
                    />
                    <div className="flex items-center gap-2 mt-1.5">
                      <label className="text-xs text-amber-300 hover:text-amber-200 cursor-pointer flex items-center gap-1 px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30" data-testid="sa-branding-upload-trigger">
                        <Upload className="w-3 h-3" /> Subir imagen
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="sa-branding-upload-input" />
                      </label>
                      {brandingEditing.logo_url && (
                        <button type="button" onClick={() => setBrandingEditing({ ...brandingEditing, logo_url: "" })} className="text-xs text-red-400 hover:text-red-300 cursor-pointer">Quitar</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Color de acento (HEX)</Label>
                <div className="flex items-center gap-2">
                  <Input value={brandingEditing.accent_color || ""} onChange={(e) => setBrandingEditing({ ...brandingEditing, accent_color: e.target.value })} placeholder="#22c55e" className="bg-white/5 border-white/10 text-white" data-testid="sa-branding-color" />
                  <input type="color" value={brandingEditing.accent_color || "#f59e0b"} onChange={(e) => setBrandingEditing({ ...brandingEditing, accent_color: e.target.value })} className="w-10 h-9 rounded cursor-pointer border border-white/10" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Slug del tenant (link único)</Label>
                <div className="flex items-center gap-1">
                  <Input
                    value={brandingEditing.subdomain || ""}
                    onChange={(e) => setBrandingEditing({ ...brandingEditing, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                    placeholder="acme"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="sa-branding-subdomain"
                  />
                  {brandingEditing.subdomain && (brandingEditing.subdomain === brandingEditing._initial_subdomain) && (
                    <Button type="button" size="sm" variant="outline" onClick={() => copyTenantLink(brandingEditing.subdomain)} className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10" data-testid="sa-copy-tenant-link">
                      <LinkIcon className="w-3 h-3 mr-1" /> Copiar link
                    </Button>
                  )}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">
                  3-32 caracteres. Tus clientes pueden entrar por:<br />
                  <code className="text-amber-300">/?tenant={brandingEditing.subdomain || "acme"}</code>{" · "}
                  <code className="text-amber-300">/t/{brandingEditing.subdomain || "acme"}/login</code>{" · "}
                  <code className="text-amber-300">{brandingEditing.subdomain || "acme"}.bo-tito.com</code> (necesita wildcard DNS)
                </p>
              </div>
              {error && <p className="text-xs text-red-300">⚠️ {error}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBrandingEditing(null)}>Cancelar</Button>
            <Button onClick={submitBranding} disabled={savingBranding} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="submit-sa-branding">
              {savingBranding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Palette className="w-4 h-4 mr-2" />}
              Guardar branding
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity dialog (root-only) */}
      <Dialog open={!!activityFor} onOpenChange={(v) => { if (!v) { setActivityFor(null); setActivityData(null); } }}>
        <DialogContent className="bg-[#13112b] border-indigo-900/30 text-white max-w-2xl" data-testid="sa-activity-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Activity className="w-4 h-4" /> Actividad de {activityFor?.email}</DialogTitle>
            <DialogDescription className="text-slate-400">Clientes que este sub-admin creó y sus solicitudes de pago.</DialogDescription>
          </DialogHeader>
          {!activityData ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-amber-400" /></div>
          ) : activityData.error ? (
            <div className="text-sm text-red-400">⚠️ {activityData.error}</div>
          ) : (
            <div className="space-y-3 max-h-[420px] overflow-y-auto">
              <div className="text-xs text-slate-400 flex items-center gap-2">
                <UsersIcon className="w-3 h-3" /> {activityData.client_count} cliente{activityData.client_count === 1 ? "" : "s"} creado{activityData.client_count === 1 ? "" : "s"}
              </div>
              {activityData.clients.length === 0 ? (
                <p className="text-sm text-slate-500">Todavía no creó ningún cliente.</p>
              ) : (
                activityData.clients.map((c) => (
                  <div key={c.id} className="bg-white/5 rounded p-3 border border-white/10 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{c.email}</p>
                      <p className="text-xs text-slate-400">{c.business_name || c.name || "—"} · creado {(c.created_at || "").slice(0, 10)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                        {c.subscription_request_count} pago{c.subscription_request_count === 1 ? "" : "s"}
                      </span>
                      <Button variant="outline" size="sm" onClick={() => { setActivityFor(null); navigate(`/admin/clients/${c.id}`); }} className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10">
                        Ver
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
